import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Search,
  MessageSquare,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  HelpCircle,
  Zap,
  X,
  Clock,
  Bot,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { AUTH_URL } from "../../constants";

const DATE_RANGES = [
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "90d", label: "Last 90 Days" },
  { value: "all", label: "All Time" },
];

const StatCard = ({ icon: Icon, label, value, color, subtitle, trend }) => (
  <div className="bg-white rounded-lg shadow-md p-6 border border-slate-200">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-slate-600 mb-1">{label}</p>
        <p className={`text-3xl font-bold ${color}`}>
          {typeof value === "number" ? value.toLocaleString() : value}
        </p>
        {subtitle && <p className="text-xs text-slate-500 mt-2">{subtitle}</p>}
      </div>
      <div
        className={`p-3 rounded-lg ${color.replace("text-", "bg-").replace("-600", "-100")}`}
      >
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
    </div>
    {trend !== undefined && (
      <div className="mt-3 flex items-center gap-1">
        {trend >= 0 ? (
          <TrendingUp className="w-3 h-3 text-green-600" />
        ) : (
          <TrendingDown className="w-3 h-3 text-red-600" />
        )}
        <span
          className={`text-xs font-medium ${trend >= 0 ? "text-green-600" : "text-red-600"}`}
        >
          {trend >= 0 ? "+" : ""}
          {trend}% vs prior period
        </span>
      </div>
    )}
  </div>
);

const ChannelBar = ({ channel, count, maxCount }) => {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  const channelColors = {
    widget: "bg-blue-500",
    api: "bg-purple-500",
    slack: "bg-green-500",
    email: "bg-amber-500",
    sms: "bg-pink-500",
    web: "bg-indigo-500",
  };
  const barColor = channelColors[channel?.toLowerCase()] || "bg-slate-500";

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-slate-700 w-20 text-right font-medium capitalize">
        {channel}
      </span>
      <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
        <div
          className={`h-full ${barColor} rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
          style={{ width: `${Math.max(pct, 4)}%` }}
        >
          {pct > 15 && (
            <span className="text-xs text-white font-medium">{count}</span>
          )}
        </div>
      </div>
      {pct <= 15 && (
        <span className="text-xs text-slate-600 font-medium w-10">{count}</span>
      )}
    </div>
  );
};

const ConversationAnalytics = () => {
  const { token } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [period, setPeriod] = useState("30d");
  const [loading, setLoading] = useState(false);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Analytics data
  const [metrics, setMetrics] = useState(null);
  const [topQuestions, setTopQuestions] = useState([]);
  const [channelBreakdown, setChannelBreakdown] = useState([]);
  const [knowledgeGaps, setKnowledgeGaps] = useState([]);

  // Gap detail modal
  const [detailGap, setDetailGap] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  // Fetch facilities on mount
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const res = await fetch(`${AUTH_URL}/facilities`, { headers });
        if (res.ok) {
          const data = await res.json();
          const list = (Array.isArray(data) ? data : []).filter(
            (f) => f.status === "active",
          );
          setFacilities(list);
        }
      } catch (err) {
        console.error("Failed to load facilities:", err);
      }
    };
    fetchFacilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch analytics when facility or period changes
  const fetchAnalytics = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${AUTH_URL}/analytics/conversations?facility_id=${encodeURIComponent(facilityId)}&period=${encodeURIComponent(period)}`,
        { headers },
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch analytics (HTTP ${res.status})`);
      }

      const data = await res.json();
      const s = data.summary || {};
      setMetrics({
        total_queries: s.total_queries || 0,
        unique_sessions: s.unique_sessions || 0,
        avg_relevance: s.avg_relevance || 0,
        low_confidence_pct: s.low_confidence_pct || 0,
      });
      setTopQuestions(data.top_questions || []);
      setChannelBreakdown(data.channel_breakdown || []);
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [facilityId, period]);

  // Fetch knowledge gaps
  const fetchKnowledgeGaps = useCallback(async () => {
    if (!facilityId) return;
    setGapsLoading(true);

    try {
      const res = await fetch(
        `${AUTH_URL}/analytics/knowledge-gaps?facility_id=${encodeURIComponent(facilityId)}&period=${encodeURIComponent(period)}`,
        { headers },
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch knowledge gaps (HTTP ${res.status})`);
      }

      const data = await res.json();
      const gaps = Array.isArray(data) ? data : [];
      gaps.sort((a, b) => (b.times_asked || 0) - (a.times_asked || 0));
      setKnowledgeGaps(gaps);
    } catch (err) {
      console.error("Error fetching knowledge gaps:", err);
    } finally {
      setGapsLoading(false);
    }
  }, [facilityId, period]); // eslint-disable-line react-hooks/exhaustive-deps

  const openGapDetail = async (gap) => {
    setDetailGap(gap);
    setDetailRows([]);
    setDetailLoading(true);
    try {
      const res = await fetch(
        `${AUTH_URL}/analytics/knowledge-gaps/detail?facility_id=${encodeURIComponent(facilityId)}&question=${encodeURIComponent(gap.user_question)}&period=${encodeURIComponent(period)}`,
        { headers },
      );
      if (res.ok) {
        const data = await res.json();
        setDetailRows(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error fetching gap detail:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (facilityId) {
      fetchAnalytics();
      fetchKnowledgeGaps();
    } else {
      setMetrics(null);
      setTopQuestions([]);
      setChannelBreakdown([]);
      setKnowledgeGaps([]);
    }
  }, [facilityId, period, fetchAnalytics, fetchKnowledgeGaps]);

  const handleRefresh = () => {
    fetchAnalytics();
    fetchKnowledgeGaps();
  };

  const getScoreColor = (score) => {
    if (score < 0.4) return "text-red-600";
    if (score < 0.7) return "text-amber-600";
    return "text-green-600";
  };

  const getRowBg = (score) => {
    if (score < 0.4) return "bg-red-50";
    if (score < 0.7) return "bg-amber-50";
    return "";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const facilityName =
    facilities.find((f) => f.facility_id === facilityId)?.facility_name ||
    facilityId;

  const maxChannelCount = Math.max(
    ...channelBreakdown.map((c) => c.count || 0),
    1,
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Knowledge Gap Analytics
          </h1>
          <p className="text-slate-600 mt-1">
            Conversation insights and knowledge base coverage analysis
          </p>
        </div>
        {facilityId && (
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        )}
      </div>

      {/* Controls Row */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          {/* Facility Selector */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Facility
            </label>
            <div className="relative">
              <select
                value={facilityId}
                onChange={(e) => setFacilityId(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white text-slate-800 pr-10"
              >
                <option value="">Select a facility...</option>
                {facilities.map((f) => (
                  <option key={f.facility_id} value={f.facility_id}>
                    {f.facility_name || f.facility_id}
                    {f.state ? ` (${f.state})` : ""}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Date Range Selector */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Period
            </label>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
              {DATE_RANGES.map((range) => (
                <button
                  key={range.value}
                  onClick={() => setPeriod(range.value)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    period === range.value
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* No Facility Selected */}
      {!facilityId && (
        <div className="bg-white rounded-lg shadow-md border border-slate-200 p-12 text-center">
          <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">
            Select a Facility
          </h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Choose a facility from the dropdown above to view conversation
            analytics and knowledge gap insights.
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-900">Error</h3>
            <p className="text-sm text-red-800 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Loading */}
      {facilityId && loading && !metrics && (
        <div className="bg-white rounded-lg shadow-md border border-slate-200 p-12 text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 mx-auto mb-4 animate-spin" />
          <p className="text-slate-600">
            Loading analytics for {facilityName}...
          </p>
        </div>
      )}

      {/* Metrics Cards */}
      {facilityId && metrics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={MessageSquare}
            label="Total Queries"
            value={metrics.total_queries}
            color="text-blue-600"
            subtitle="Conversation log entries"
          />
          <StatCard
            icon={Zap}
            label="Unique Sessions"
            value={metrics.unique_sessions}
            color="text-purple-600"
            subtitle="Distinct session IDs"
          />
          <StatCard
            icon={TrendingUp}
            label="Avg Relevance Score"
            value={`${(metrics.avg_relevance * 100).toFixed(1)}%`}
            color="text-green-600"
            subtitle="Mean similarity score"
          />
          <StatCard
            icon={AlertTriangle}
            label="Low Confidence Rate"
            value={`${metrics.low_confidence_pct.toFixed(1)}%`}
            color={
              metrics.low_confidence_pct > 30
                ? "text-red-600"
                : metrics.low_confidence_pct > 15
                  ? "text-amber-600"
                  : "text-green-600"
            }
            subtitle="Queries with score < 0.7"
          />
        </div>
      )}

      {/* No Data State */}
      {facilityId && metrics && metrics.total_queries === 0 && !loading && (
        <div className="bg-white rounded-lg shadow-md border border-slate-200 p-12 text-center">
          <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">
            No Data Yet
          </h3>
          <p className="text-slate-500 max-w-md mx-auto">
            No conversation logs found for {facilityName} in the selected
            period. Logs will appear here once users start interacting with the
            chatbot.
          </p>
        </div>
      )}

      {/* Knowledge Gaps Table */}
      {facilityId && metrics && metrics.total_queries > 0 && (
        <div className="bg-white rounded-lg shadow-md border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">
                    Knowledge Gaps
                  </h3>
                  <p className="text-sm text-slate-500">
                    Questions the knowledge base could not answer well
                  </p>
                </div>
              </div>
              {gapsLoading && (
                <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            {knowledgeGaps.length === 0 && !gapsLoading ? (
              <div className="p-8 text-center text-slate-500">
                <Filter className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm">
                  No knowledge gaps detected. Your KB is covering queries well.
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Question
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Times Asked
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Avg Score
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Channel
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Last Asked
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {knowledgeGaps.map((gap, idx) => {
                    const score = gap.similarity_score || 0;
                    const status =
                      score < 0.4
                        ? "Critical"
                        : score < 0.7
                          ? "Needs Content"
                          : "Acceptable";
                    const statusBg =
                      score < 0.4
                        ? "bg-red-100 text-red-700"
                        : score < 0.7
                          ? "bg-amber-100 text-amber-700"
                          : "bg-green-100 text-green-700";

                    return (
                      <tr
                        key={idx}
                        onClick={() => openGapDetail(gap)}
                        className={`${getRowBg(score)} hover:bg-blue-50 cursor-pointer transition-colors`}
                      >
                        <td className="px-6 py-3 text-sm text-slate-800 max-w-xs">
                          <p className="truncate" title={gap.user_question}>
                            {gap.user_question}
                          </p>
                        </td>
                        <td className="px-6 py-3 text-sm text-center font-medium text-slate-700">
                          {gap.times_asked || 1}
                        </td>
                        <td className="px-6 py-3 text-sm text-center">
                          <span
                            className={`font-semibold ${getScoreColor(score)}`}
                          >
                            {(score * 100).toFixed(0)}%
                          </span>
                        </td>
                        <td className="px-6 py-3 text-sm text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 capitalize">
                            {gap.top_channel || "widget"}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-sm text-center text-slate-600">
                          {formatDate(gap.last_asked)}
                        </td>
                        <td className="px-6 py-3 text-center">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBg}`}
                          >
                            {status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Gap Detail Modal */}
      {detailGap && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <div className="flex-1 pr-4">
                <h3 className="text-base font-semibold text-slate-900">
                  Gap Detail
                </h3>
                <p
                  className="text-sm text-slate-600 mt-1 line-clamp-2"
                  title={detailGap.user_question}
                >
                  {detailGap.user_question}
                </p>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <span className="text-xs text-slate-500">
                    Asked{" "}
                    <span className="font-medium text-slate-700">
                      {detailGap.times_asked}x
                    </span>{" "}
                    in this period
                  </span>
                  <span className="text-xs text-slate-500">
                    Avg score:{" "}
                    <span
                      className={`font-medium ${getScoreColor(detailGap.similarity_score || 0)}`}
                    >
                      {((detailGap.similarity_score || 0) * 100).toFixed(0)}%
                    </span>
                  </span>
                  {detailGap.first_asked && (
                    <span className="text-xs text-slate-500">
                      First: {formatDate(detailGap.first_asked)}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setDetailGap(null)}
                className="p-1.5 text-slate-400 hover:text-slate-700 flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {detailLoading ? (
                <div className="flex items-center justify-center py-10">
                  <RefreshCw className="w-5 h-5 text-blue-500 animate-spin mr-2" />
                  <span className="text-sm text-slate-500">
                    Loading occurrences...
                  </span>
                </div>
              ) : detailRows.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">
                  No details available.
                </p>
              ) : (
                detailRows.map((row, i) => (
                  <div
                    key={row.id || i}
                    className="border border-slate-200 rounded-lg p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="w-3.5 h-3.5" />
                        {row.created_at
                          ? new Date(row.created_at).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })
                          : "Unknown time"}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 capitalize">
                          {row.channel}
                        </span>
                        {row.similarity_score !== null && (
                          <span
                            className={`text-xs font-semibold ${getScoreColor(row.similarity_score || 0)}`}
                          >
                            {((row.similarity_score || 0) * 100).toFixed(0)}%
                            match
                          </span>
                        )}
                        {row.response_time_ms && (
                          <span className="text-xs text-slate-400">
                            {row.response_time_ms}ms
                          </span>
                        )}
                      </div>
                    </div>
                    {row.ai_response && (
                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Bot className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-xs font-medium text-slate-600">
                            AI Response
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed line-clamp-4">
                          {row.ai_response}
                        </p>
                      </div>
                    )}
                    {Array.isArray(row.sources_used) &&
                      row.sources_used.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-1">
                            Sources used
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {row.sources_used.map((src, si) => (
                              <span
                                key={si}
                                className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full"
                              >
                                {typeof src === "string"
                                  ? src
                                  : src.title || src.id || "Source"}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    {(!row.sources_used || row.sources_used.length === 0) && (
                      <p className="text-xs text-red-500">
                        No KB sources matched this query.
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Top Questions Table */}
      {facilityId && topQuestions.length > 0 && (
        <div className="bg-white rounded-lg shadow-md border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  Top Questions
                </h3>
                <p className="text-sm text-slate-500">
                  Most frequently asked questions in the selected period
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Question
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Count
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Avg Score
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Channel
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {topQuestions.map((q, idx) => {
                  const score = q.avg_score || 0;
                  return (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-3 text-sm text-slate-800 max-w-md">
                        <p className="truncate" title={q.question}>
                          {q.question}
                        </p>
                      </td>
                      <td className="px-6 py-3 text-sm text-center font-medium text-slate-700">
                        {q.count || 0}
                      </td>
                      <td className="px-6 py-3 text-sm text-center">
                        <span
                          className={`font-semibold ${getScoreColor(score)}`}
                        >
                          {(score * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                          {q.channel || "unknown"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Channel Breakdown */}
      {facilityId && channelBreakdown.length > 0 && (
        <div className="bg-white rounded-lg shadow-md border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <Filter className="w-5 h-5 text-indigo-600" />
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  Channel Breakdown
                </h3>
                <p className="text-sm text-slate-500">
                  Query volume by channel source
                </p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {channelBreakdown
              .sort((a, b) => (b.count || 0) - (a.count || 0))
              .map((ch, idx) => (
                <ChannelBar
                  key={idx}
                  channel={ch.channel}
                  count={ch.count || 0}
                  maxCount={maxChannelCount}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationAnalytics;
